import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { buildWarningAreaGeometry, DEFAULT_SVG_DIMS } from '../../utils/warningAreaGeometry';

interface WarningAreaMiniMapProps {
  /** GeoJSON-geometri fra farevarselet (feature.geometry pakket i en Feature). */
  polygon?: GeoJSON.Feature;
  severity: 'Yellow' | 'Orange' | 'Red';
  /** Kartsenter — tegnes som «du er her»-prikk hvis innenfor. */
  center?: { lat: number; lon: number } | null;
  /** Områdenavn brukt til aria-label. */
  areaName?: string;
}

const SEVERITY_COLORS: Record<string, { fill: string; stroke: string }> = {
  Yellow: { fill: '#eab308', stroke: '#a16207' }, // yellow-500 / yellow-700
  Orange: { fill: '#f97316', stroke: '#c2410c' }, // orange-500 / orange-700
  Red: { fill: '#ef4444', stroke: '#b91c1c' }, // red-500 / red-700
};

/**
 * Statisk SVG-illustrasjon av det berørte området for et farevarsel.
 * Tegner varselpolygonet fylt i alvorlighetsfargen (markering av området)
 * pluss en valgfri «du er her»-prikk. Krever ingen kartfliser eller nettverk —
 * polygonet ligger allerede i minnet fra farevarsel-tjenesten.
 */
const WarningAreaMiniMap: React.FC<WarningAreaMiniMapProps> = ({
  polygon,
  severity,
  center,
  areaName,
}) => {
  const { t } = useTranslation();
  const geo = useMemo(
    () => buildWarningAreaGeometry(polygon?.geometry, center),
    [polygon, center],
  );

  if (!geo) return null;

  const colors = SEVERITY_COLORS[severity] || SEVERITY_COLORS.Yellow;
  const label = areaName
    ? t('fire_danger.area_map_label', { area: areaName })
    : t('fire_danger.area_map_label_generic');

  return (
    <svg
      viewBox={geo.viewBox}
      width="100%"
      role="img"
      aria-label={label}
      className="mt-2 rounded-xl border border-black/10 bg-white/60"
      style={{ display: 'block', height: 'auto' }}
      preserveAspectRatio="xMidYMid meet"
    >
      {/* Berørt område */}
      <path
        d={geo.pathData}
        fill={colors.fill}
        fillOpacity={0.35}
        fillRule="evenodd"
        stroke={colors.stroke}
        strokeWidth={1.75}
        strokeLinejoin="round"
      />
      {/* «Du er her»-prikk */}
      {geo.dot && (
        <g>
          <circle cx={geo.dot.cx} cy={geo.dot.cy} r={5.5} fill="#ffffff" />
          <circle cx={geo.dot.cx} cy={geo.dot.cy} r={3.5} fill="#2563eb" />
        </g>
      )}
    </svg>
  );
};

export default WarningAreaMiniMap;
